
import { Component, Vue } from "vue-property-decorator";
import FeedDeliverySummary from "@/views/FeedDeliverySummary.vue";
import CribsSummaryPlan from "./CribPlan.vue";
import FeedSchedule from "./FeedSchedule.vue";
import { createClient } from "@/helpers/graphql-ws-rtc-adapter";

import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";

const Subscriber = require("@jetblack/graphql-reconnect-client");

@Component({
  components: {
    FeedDeliverySummary,
    CribsSummaryPlan,
    FeedSchedule,
    BreadcrumbTrail,
  },
})
export default class Replay extends Vue {
  defaultDateSelected = this.formatDate(new Date());
  dateSelected = this.formatPickerDate(new Date());
  dateMenu = false;
  frameTimeRaw: any = undefined;
  activeTab = 0;
  tabs = [
    { title: "Ration Summary", icon: "mdi-chart-box-outline" },
    { title: "Crib Scores", icon: "mdi-altimeter" },
    { title: "Feeding Schedule", icon: "mdi-nutrition" },
  ];

  mounted() {
    this.$store.dispatch("user/addFirebaseCallback", this.getReplayFrameSocket);
  }

  destroyed() {
    if (this.graphQLUnsubscribe) {
      this.graphQLUnsubscribe();
    }
    if (this.graphQLSubscriber) {
      this.graphQLSubscriber();
    }
  }

  isPlaying = false;

  togglePlay() {
    this.isPlaying = !this.isPlaying;
    if (this.isPlaying) {
      this.startPlayback();
    } else {
      this.pausePlayback();
    }
  }

  get frameTimeDisplay() {
    if (!this.frameTimeRaw) return "Waiting for frame";
    const frameDate = this.parseFrameDate(this.frameTimeRaw);
    if (!frameDate) return String(this.frameTimeRaw);
    return this.$store.state.moment(frameDate).format("DD MMM YYYY HH:mm:ss");
  }

  get replayFrameSubscription() {
    return `subscription($guid: String!) {
      replaycribScores(guid: $guid) {
        typename
        guid
        date
      }
    }`;
  }

  get replayVariables() {
    return { guid: this.$store.state.user.location.guid };
  }

  graphQLSubscriber: any = undefined;
  graphQLUnsubscribe: any = undefined;
  urlws = "wss://pi.gigalot.systems:7778/api/ws";
  options = {};
  operationName = null;

  async getReplayFrameSocket() {
    if (this.$store.state.useP2PConn) {
      await this.getOnlineReplayFrameSocket();
    } else {
      await this.getOnsiteReplayFrameSocket();
    }
  }

  updateFrameTimeFromReplayData(data: any) {
    const frameTime = data?.replaycribScores?.date;
    if (frameTime !== undefined && frameTime !== null && frameTime !== "") {
      this.frameTimeRaw = frameTime;
    }
  }

  async getOnsiteReplayFrameSocket() {
    this.graphQLSubscriber = Subscriber.graphQLReconnectingSubscriber(
      this.urlws,
      this.options,
      (error: any, subscribe: any) => {
        try {
          if (!(error || subscribe)) {
            this.$store.state.wsDialogVisible = false;
            return;
          }
          if (error) {
            if (error.event.type !== "close") {
              this.$store.state.wsDialogVisible = true;
              console.error(error);
            }
          }
          this.graphQLUnsubscribe = subscribe(this.replayFrameSubscription, this.replayVariables, this.operationName, (error: any, data: any) => {
            if (!(error || subscribe)) {
              this.$store.state.wsDialogVisible = false;
              return;
            }
            if (error) {
              if (error.event.type !== "close") {
                this.$store.state.wsDialogVisible = true;
                console.error(error);
              }
              return;
            }
            this.$store.state.wsDialogVisible = false;
            this.updateFrameTimeFromReplayData(data);
          });
        } catch (err) {
          console.log(err);
        }
      },
      15000,
      3,
      "graphql-ws"
    );
  }

  async getOnlineReplayFrameSocket() {
    const variables = { guid: this.$store.state.user.location.guid, user: this.$store.state.user.user.uid };

    const subscribe = (subscriptions: string, variables: any, callback: (error: any, data: any) => Promise<void>) => {
      const client = createClient();
      let unsubscribe = client?.subscribe(
        {
          query: subscriptions,
          variables: variables,
        },
        {
          next: (message: any) => callback(undefined, message.data),
          error: (err: any) => {
            throw err;
          },
          complete: () => {
            console.log("subscription completed");
          },
        }
      );
      return unsubscribe;
    };

    this.graphQLUnsubscribe = subscribe(this.replayFrameSubscription, variables, async (error: any, data: any) => {
      this.$store.state.wsConnected = true;
      if (!(error || subscribe)) {
        this.$store.state.wsDialogVisible = false;
        return;
      }
      if (error) {
        this.$store.state.wsConnected = false;
        if (error.event.type !== "close") {
          this.$store.state.wsDialogVisible = true;
          console.error(error);
        }
        return;
      }
      this.$store.state.wsDialogVisible = false;
      this.$store.state.wsConnected = true;
      this.updateFrameTimeFromReplayData(data);
    });
  }

  async sendPlaybackCommand(path: string) {
    let url = `${this.$store.getters["backendUrl"]()}/${path}`;
    let res = await fetch(url, {
      method: "GET",
    });
    const text = await res.text();
    if (!res.ok) throw Error("Response OK : " + text);
    this.updateFrameTimeFromPlaybackResponse(text);
  }

  updateFrameTimeFromPlaybackResponse(responseText: string) {
    if (!responseText) return;
    const parsed = this.tryParseJson(responseText);
    const frameTime = this.findFrameTime(parsed ?? responseText);
    if (frameTime) {
      this.frameTimeRaw = frameTime;
    }
  }

  tryParseJson(responseText: string) {
    try {
      return JSON.parse(responseText);
    } catch {
      return undefined;
    }
  }

  findFrameTime(value: any): any {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "string" || typeof value === "number") {
      if (this.parseFrameDate(value)) return value;
      if (typeof value !== "string") return undefined;
      const embeddedTime = value.match(/\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?|\d{2}-\d{2}-\d{4}[ T]\d{2}:\d{2}(?::\d{2})?/);
      return embeddedTime && this.parseFrameDate(embeddedTime[0]) ? embeddedTime[0] : undefined;
    }
    const keys = ["frameTime", "replayTime", "playbackTime", "currentTime", "timestamp", "time", "date", "frameDate", "playbackDate"];
    for (const key of keys) {
      if (value[key] && this.parseFrameDate(value[key])) {
        return value[key];
      }
    }
    for (const nestedValue of Object.values(value)) {
      const frameTime = this.findFrameTime(nestedValue);
      if (frameTime) return frameTime;
    }
    return undefined;
  }

  parseFrameDate(value: any): Date | undefined {
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (typeof value === "number") {
      const date = new Date(value < 100000000000 ? value * 1000 : value);
      return Number.isNaN(date.getTime()) ? undefined : date;
    }
    if (typeof value !== "string") return undefined;

    const trimmed = value.trim();
    if (!trimmed) return undefined;

    if (/^\d+$/.test(trimmed)) {
      return this.parseFrameDate(Number(trimmed));
    }

    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) return date;

    const isoLikeDate = new Date(trimmed.replace(" ", "T"));
    if (!Number.isNaN(isoLikeDate.getTime())) return isoLikeDate;

    const dayFirstMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (!dayFirstMatch) return undefined;
    const [, day, month, year, hour = "00", minute = "00", second = "00"] = dayFirstMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  async startPlayback() {
    await this.sendPlaybackCommand("startPlayback");
  }

  async pausePlayback() {
    await this.sendPlaybackCommand("pausePlayback");
  }

  async stepForward() {
    await this.sendPlaybackCommand("stepForward");
  }
  async stepBack() {
    await this.sendPlaybackCommand("stepBack");
  }

  async fastForward() {
    await this.sendPlaybackCommand("fastForward");
  }
  async rewind() {
    await this.sendPlaybackCommand("rewind");
  }

  async loadReplay() {
    console.log(`Date selected: ${this.dateSelected}`);
    if (this.dateSelected.indexOf("-") == 4) {
      this.defaultDateSelected = this.reverseDateSelected(this.dateSelected);
    }
    let url = `${this.$store.getters["backendUrl"]()}/loadPlayback?date=${this.defaultDateSelected}`;
    let res = await fetch(url, {
      method: "GET",
    });
    const text = await res.text();
    if (!res.ok) throw Error("Response OK : " + text);
    this.updateFrameTimeFromPlaybackResponse(text);
  }

  reverseDateSelected(date: string) {
    return `${date.slice(8, 10)}-${date.slice(5, 7)}-${date.slice(0, 4)}`;
  }

  onDateSelected() {
    this.dateMenu = false;
    this.loadReplay();
  }

  formatDate(date: any) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatPickerDate(date: Date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "Replay", disabled: true });
  }
}
